import { Button } from '@dev-spendesk/grapes';
import React, { useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { logger } from 'src/utils/datadog-log-wrapper';

export interface ShowQrCodeProps {
  qrCodeUrl: string;
  secretKey: string;
}

function formatSecretKey(secret: string) {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}

export const ShowQrCode = ({ qrCodeUrl, secretKey }: ShowQrCodeProps) => {
  const { t } = useTranslation('global');
  const [isCopied, setIsCopied] = useState(false);

  const [showManualEntryInstructions, setShowManualEntryInstructions] =
    useState(false);

  const onCopyClicked = () => async () => {
    try {
      await navigator.clipboard.writeText(secretKey);
      setIsCopied(true);
    } catch {
      // ignore error thrown
      logger.info('Failed to copy TOTP secret key to clipboard', {
        scope: 'user-authentication-factors',
        team: 'cards-ux',
      });
    }
  };

  const handleToggleShowManualEntryInstructions = () => {
    if (showManualEntryInstructions) {
      setShowManualEntryInstructions(false);
    } else {
      setShowManualEntryInstructions(true);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      {!showManualEntryInstructions && (
        <>
          <p className="text-content-primary body-m ms-32">
            {t('profile.authenticationFactors.create.totp.scanQrCodeItemTitle')}
          </p>
          <div className="bg-background-secondary-default rounded-8 mt-8 flex h-[300px] w-full flex-col items-center justify-center px-16 py-24">
            <img
              src={qrCodeUrl}
              className="text-content-tertiary body-s mb-8 h-[200px] w-[200px] text-center"
              alt={secretKey}
            />
          </div>
          <Button
            variant="tertiaryNeutral"
            text={t('profile.authenticationFactors.create.totp.cantScan')}
            onClick={handleToggleShowManualEntryInstructions}
          />
        </>
      )}

      {showManualEntryInstructions && (
        <>
          <p className="text-content-primary body-m ms-32">
            {t(
              'profile.authenticationFactors.create.totp.manualSetupItemTitle',
            )}
          </p>
          <div className="bg-background-secondary-default rounded-8 flex w-full items-center justify-between py-8 ps-32 pe-16">
            <p className="text-content-primary body-m flex-1 font-mono">
              {formatSecretKey(secretKey)}
            </p>
            <Button
              variant="tertiaryBrand"
              text={isCopied ? t('misc.copied') : t('misc.copy')}
              onClick={onCopyClicked()}
            />
          </div>
          <Button
            variant="tertiaryNeutral"
            text={t(
              'profile.authenticationFactors.create.totp.openAppNextButton',
            )}
            onClick={handleToggleShowManualEntryInstructions}
          />
        </>
      )}
    </div>
  );
};
